import { evaluateFormula } from "@yjsgames/formula-evaluator";
import { MODULE_BRICK_BALANCE } from "../data/balance.js";

export interface RebirthState {
  readonly rebirthPoint: number;
  /** 누적 환생 횟수 — 환생 가능 조건 누진(`({rebirthCount}+1)*10`)의 입력. */
  readonly rebirthCount: number;
}

export interface RebirthPointBreakdown {
  readonly basePoints: number;
  readonly bonusPoints: number;
  readonly totalPoints: number;
}

/**
 * 환생 — 순수 로직 (단위 테스트 대상). 진행 초기화는 GameScene이 수행하고,
 * 본 컨트롤러는 환생 포인트 적립/소비 + 환생 횟수 + (global.tsv expression 기반) 조건·획득량 계산만 담당한다.
 *
 * 영구 스탯 강화(공격력/쿨타임/골드획득/포인트보너스)는 환생 포인트를 소비하며,
 * StatUpgradeController(REBIRTH_UPGRADE_DEFS 주입)가 레벨/효과를 관리한다 — 본 컨트롤러는 spendPoint로 차감만 한다.
 * (구 permanentDamageBonus/buyPermanentDamage는 환생 "공격력" 스탯으로 통합 흡수 — v8 마이그레이션에서 레벨 이관.)
 */
export class RebirthController {
  private _point: number;
  private _count: number;

  constructor(initial?: RebirthState) {
    this._point = initial?.rebirthPoint ?? 0;
    this._count = initial?.rebirthCount ?? 0;
  }

  get rebirthPoint(): number {
    return this._point;
  }

  get rebirthCount(): number {
    return this._count;
  }

  /**
   * 현재 환생 횟수 기준 환생 가능 최소 챕터 = evaluateFormula(rebirthChapterExpr, {rebirthCount}).
   * 첫 환생은 챕터 10. 환생 탭(챕터 6) 해금 뒤 목표를 보여주는 예고 구간을 둔다.
   */
  requiredChapter(): number {
    return evaluateFormula(MODULE_BRICK_BALANCE.rebirthChapterExpr, { rebirthCount: this._count });
  }

  /** 환생 0회차 기준 초기 요구 챕터. 상점 상품 설명/구매 후 결과 표시에서 공유한다. */
  initialRequiredChapter(): number {
    return evaluateFormula(MODULE_BRICK_BALANCE.rebirthChapterExpr, { rebirthCount: 0 });
  }

  /** 환생 가능 조건 — 현재 챕터 ≥ requiredChapter(환생 횟수 누진). */
  canRebirth(currentChapter: number): boolean {
    return currentChapter >= this.requiredChapter();
  }

  /**
   * 환생 시 적립되는 기본 포인트 = floor(evaluateFormula(rebirthPointExpr, {bestChapter})).
   * evaluateFormula는 floor 미지원이라 여기서 Math.floor로 래핑한다. 포인트 보너스 강화는 performRebirth에서 별도 가산.
   */
  gainedBasePoints(bestChapter: number): number {
    return Math.floor(evaluateFormula(MODULE_BRICK_BALANCE.rebirthPointExpr, { bestChapter }));
  }

  /** UI/확인 팝업/실제 실행이 같은 기본+보너스 분해를 쓰게 하는 단일 계산 헬퍼. */
  gainedPointBreakdown(bestChapter: number, bonusPoints = 0): RebirthPointBreakdown {
    const basePoints = this.gainedBasePoints(bestChapter);
    const normalizedBonus = Math.max(0, Math.floor(bonusPoints));
    return {
      basePoints,
      bonusPoints: normalizedBonus,
      totalPoints: basePoints + normalizedBonus,
    };
  }

  /**
   * 환생 실행 — 환생 횟수 +1 + (기본 적립 + bonusPoints) 포인트 적립. 반환: 이번에 획득한 총 포인트.
   * bonusPoints = 환생 포인트 보너스 강화 효과(GameScene이 rebirthUpgrade.effectOf("pointBonus") 전달, 가산식).
   */
  performRebirth(bestChapter: number, bonusPoints = 0): number {
    const gained = this.gainedPointBreakdown(bestChapter, bonusPoints).totalPoints;
    this._point += gained;
    this._count += 1;
    return gained;
  }

  /**
   * 다음 환생 요구 조건을 초기 챕터로 되돌린다.
   * 환생 포인트와 환생 강화는 유지하고, 요구 챕터 산식의 누진 입력만 0회차로 낮춘다.
   */
  resetRequiredChapterToInitial(): boolean {
    if (this._count <= 0) return false;
    this._count = 0;
    return true;
  }

  /** 보상 등으로 환생 포인트 직접 적립 (던전/리워드 통합). 음수는 무시. */
  addPoint(n: number): void {
    this._point += Math.max(0, Math.floor(n));
  }

  /** 환생 스탯 강화용 포인트 차감 — 보유 포인트 ≥ n이면 차감 + true, 부족/비유한이면 무변경 + false. */
  spendPoint(n: number): boolean {
    const cost = Math.max(0, Math.floor(n));
    if (!Number.isFinite(cost) || this._point < cost) return false; // NaN/Infinity 방어(차감 오염 차단)
    this._point -= cost;
    return true;
  }

  toState(): RebirthState {
    return { rebirthPoint: this._point, rebirthCount: this._count };
  }
}
